import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from './supabase'

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  session: Session | null
  isTOTPVerified: boolean
  setIsTOTPVerified: (verified: boolean) => void
  totpAttemptCount: number
  resetTOTPAttempts: () => void
  hasTOTPRegistered: boolean
  setHasTOTPRegistered: (registered: boolean) => void
  isInitializing: boolean
  setIsInitializing: (initializing: boolean) => void
  isRecoverySession: boolean
  setIsRecoverySession: (recovery: boolean) => void
  resetRecoverySession: () => void
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<{ session: Session | null, user: User | null }>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
  getToken: () => string | null
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isTOTPVerified, setIsTOTPVerified] = useState(false)
  const [hasTOTPRegistered, setHasTOTPRegistered] = useState(false)
  const [totpAttemptCount, setTOTPAttemptCount] = useState(0)
  const [isInitializing, setIsInitializing] = useState(true)
  const [isRecoverySession, setIsRecoverySession] = useState(false)
  const [isInviteSession, setIsInviteSession] = useState(false)

  const resetTOTPAttempts = () => setTOTPAttemptCount(0)
  const resetRecoverySession = () => setIsRecoverySession(false)
  const resetInviteSession = () => setIsInviteSession(false)

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsInitializing(true)
        console.log('[AuthContext] Starting auth initialization')

        // Check active sessions and sets the user
        const { data: { session } } = await supabase.auth.getSession()
        
        if (!isAuthenticated) {
          setIsAuthenticated(!!session)
          setUser(session?.user ?? null)
          setSession(session)
          // Reset TOTP verification on new session
          setIsTOTPVerified(false)
          resetTOTPAttempts()

          // Check TOTP registration status
          if (session?.user) {
            try {
              const { data: factors } = await supabase.auth.mfa.listFactors()
              setHasTOTPRegistered(factors.totp && factors.totp.length > 0)
              
              // Fetch user profile on initialization if we have a valid session
              if (session.access_token) {
                await fetchUserProfile(session.access_token)
              }
            } catch (error) {
              console.error('Error checking TOTP registration status:', error)
              setHasTOTPRegistered(false)
            }
          } else {
            setHasTOTPRegistered(false)
          }
        }
      } finally {
        setIsInitializing(false)
        console.log('[AuthContext] Auth initialization complete:', { 
          isAuthenticated: !!session,
          hasUser: !!session?.user
        })
      }
    }

    initializeAuth()

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        console.log('[AuthContext] Auth state change:', event)
        
        // Handle session expiry and sign out events
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Token refreshed successfully')
          
          // Ensure profile exists after token refresh
          if (session?.access_token) {
            await fetchUserProfile(session.access_token)
          }
        } else if (event === 'SIGNED_IN') {
          console.log('[AuthContext] User signed in')
          
          // Check if this is a recovery session by examining the current URL
          const urlParams = new URLSearchParams(window.location.search)
          const hashParams = new URLSearchParams(window.location.hash.replace('#', ''))
          const isRecoveryLink = urlParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery'
          
          // Parse redirect_to parameter to check if it points to reset password page
          const redirectTo = urlParams.get('redirect_to')
          const decodedRedirectTo = redirectTo ? decodeURIComponent(redirectTo) : null
          const redirectToResetPassword = decodedRedirectTo && 
            (decodedRedirectTo.includes('/auth/reset-password') || 
             decodedRedirectTo.includes('#/auth/reset-password'))
          
          // Also check if user is on reset password page - likely a recovery session
          const currentPath = window.location.pathname
          const isOnResetPasswordPage = currentPath === '/auth/reset-password' || 
                                        currentPath.includes('/auth/reset-password') || 
                                        currentPath === '/reset-password' ||
                                        window.location.hash.includes('/auth/reset-password')
          
          console.log('[AuthContext] Reset password path check - currentPath:', currentPath, 
                      'hash:', window.location.hash, 
                      'isOnResetPasswordPage:', isOnResetPasswordPage);
          
          if (isRecoveryLink || redirectToResetPassword || isOnResetPasswordPage) {
            console.log('[AuthContext] Detected recovery session on sign in, recovery link:', isRecoveryLink, 'redirect to reset page:', redirectToResetPassword, 'on reset page:', isOnResetPasswordPage)
            setIsRecoverySession(true)
            
            // Navigate to reset password page if not already there
            if (!isOnResetPasswordPage) {
              console.log('[AuthContext] Redirecting to reset password page')
              window.location.replace('/auth/reset-password')
            }
          }
          
          // Ensure profile exists after sign in
          if (session?.access_token) {
            await fetchUserProfile(session.access_token)
          }
        } else if (event === 'SIGNED_OUT' || event === 'TOKEN_EXPIRED') {
          console.log('[AuthContext] Session expired or user signed out')
          // Store the current URL for redirect after login
          const currentPath = window.location.pathname + window.location.search
          if (currentPath !== '/login') {
            localStorage.setItem('redirectUrl', currentPath)
          }
        }

        setIsAuthenticated(!!session)
        setUser(session?.user ?? null)
        setSession(session)
        // Reset TOTP verification on auth state changes
        if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Resetting TOTP verification')
          setIsTOTPVerified(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const refreshSession = async () => {
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (error) throw error
    setSession(session)
    setUser(session?.user ?? null)
    setIsAuthenticated(!!session)
  }

  const getToken = (): string | null => {
    return session?.access_token ?? null
  }

  // Fetch user profile to ensure it's created
  const fetchUserProfile = async (token: string) => {
    try {
      console.log('[AuthContext] Fetching user profile to ensure it exists')
      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/profile/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        console.log('[AuthContext] User profile fetched/created successfully')
        return await response.json()
      } else {
        console.error('[AuthContext] Failed to fetch/create user profile:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('[AuthContext] Error fetching user profile:', error)
    }
  }

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error

    // Check TOTP registration status after successful login
    if (data.session) {
      const { data: factors } = await supabase.auth.mfa.listFactors()
      setHasTOTPRegistered(factors.totp && factors.totp.length > 0)
      
      // Fetch user profile immediately after login to ensure it's created
      await fetchUserProfile(data.session.access_token)
    }
  }

  const signup = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    if (error) throw error
    return { session: data.session, user: data.user }
  }

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.warn('Error during signOut:', error.message)
      }
      // Always clear local state regardless of API errors
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)
      setHasTOTPRegistered(false)
      resetTOTPAttempts()
      resetRecoverySession()
      resetInviteSession()
    } catch (error) {
      console.error('Exception during logout:', error)
      // Still clear local state even if there's an exception
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)
      setHasTOTPRegistered(false)
      resetTOTPAttempts()
      resetRecoverySession()
      resetInviteSession()
    }
  }
  
  const changePassword = async (currentPassword: string, newPassword: string) => {
    // First verify the current password by attempting to sign in
    if (!user?.email) {
      throw new Error('User email not available')
    }
    
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    })
    
    if (verifyError) throw verifyError
    
    // Then update the password
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })
    
    if (error) throw error
    
    // Refresh the session after password change
    await refreshSession()
    
    console.log('[AuthContext] Password changed successfully')
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      session,
      isTOTPVerified,
      setIsTOTPVerified,
      hasTOTPRegistered,
      setHasTOTPRegistered,
      totpAttemptCount,
      resetTOTPAttempts,
      isInitializing,
      setIsInitializing,
      isRecoverySession,
      setIsRecoverySession,
      resetRecoverySession,
      isInviteSession,
      setIsInviteSession,
      resetInviteSession,
      login, 
      signup, 
      logout,
      refreshSession,
      getToken,
      changePassword
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}